import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Search, Users, Crown, Lock, Globe, Loader2, ChevronLeft, ChevronRight,
  LogIn
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import EngagementLockModal, { useEngagementStatus } from "../components/EngagementLockModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicLeaguesPage() {
  const { user, getAuthHeader, dbUser } = useAuth();
  const navigate = useNavigate();
  
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("members");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [requestingId, setRequestingId] = useState(null);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const engagement = useEngagementStatus();

  const fetchPublicLeagues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        sort
      });
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/leagues/public?${params.toString()}`);
      setLeagues(res.data.leagues || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch public leagues:", e);
    } finally {
      setLoading(false);
    }
  }, [page, sort, search]);

  useEffect(() => {
    fetchPublicLeagues();
  }, [fetchPublicLeagues]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPublicLeagues();
  };

  const requestToJoin = async (leagueId, leagueName) => {
    if (!user) {
      navigate("/auth", { state: { returnTo: "/leagues" } });
      return;
    }

    setRequestingId(leagueId);
    setError("");
    setSuccess("");

    try {
      await axios.post(`${API}/leagues/${leagueId}/request-join`, {}, { headers: getAuthHeader() });
      setSuccess(`Join request sent for "${leagueName}". The organizer will review it.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send join request");
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <div>
    <div className="min-h-screen bg-slate-900 text-white pb-24" data-testid="public-leagues-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Link to="/my-leagues" className="text-slate-400 hover:text-white">
              <ChevronLeft size={24} />
            </Link>
            <div>
              <h1 className="text-lg font-bold">Public Leagues</h1>
              <p className="text-slate-500 text-xs">{total} leagues available</p>
            </div>
          </div>
          <Globe className="text-emerald-400" size={24} />
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search by league or organizer name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500"
              data-testid="search-input"
            />
          </div>
          <button 
            type="submit" 
            className="px-4 py-2 bg-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-500 transition-all"
          >
            Search
          </button>
        </form>

        {/* Sort */}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => { setSort("members"); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              sort === "members" ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"
            }`}
          >
            Most Members
          </button>
          <button
            onClick={() => { setSort("created_at"); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              sort === "created_at" ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"
            }`}
          >
            Newest
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mx-4 mt-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-sm">
          {success}
        </div>
      )}

      {/* Leagues List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-emerald-400" size={32} />
          </div>
        ) : leagues.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="mx-auto text-slate-600 mb-3" size={48} />
            <p className="text-slate-400">No public leagues found</p>
            <p className="text-slate-500 text-sm mt-1">Try a different search term</p>
          </div>
        ) : (
          leagues.map(league => (
            <div 
              key={league.id}
              className={`p-4 rounded-xl border transition-all ${
                league.is_full 
                  ? "bg-slate-800/30 border-slate-700/50 opacity-60" 
                  : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
              }`}
              data-testid={`league-card-${league.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{league.name}</h3>
                    {league.is_full && (
                      <span className="px-2 py-0.5 bg-slate-600 text-slate-300 rounded text-xs">Full</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                    <Crown size={14} className="text-amber-400" />
                    <span>{league.organizer_name}</span>
                  </div>
                  {league.description && (
                    <p className="text-slate-500 text-sm mt-2 line-clamp-2">
                      {league.description.substring(0, 100)}{league.description.length > 100 ? "..." : ""}
                    </p>
                  )}
                </div>
                <div className="text-right ml-4">
                  <div className="flex items-center gap-1 text-slate-400">
                    <Users size={14} />
                    <span className="text-sm font-medium">
                      {league.member_count}/{league.max_members}
                    </span>
                  </div>
                </div>
              </div>

              {/* Join Button */}
              {!league.is_full && (
                <button
                  onClick={() => requestToJoin(league.id, league.name)}
                  disabled={requestingId === league.id}
                  className="w-full mt-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid={`join-btn-${league.id}`}
                >
                  {requestingId === league.id ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : user ? (
                    <>Request to Join</>
                  ) : (
                    <>
                      <LogIn size={16} />
                      Login to Join
                    </>
                  )}
                </button>
              )}
            </div>
          ))
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-slate-400 text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      <EngagementLockModal
        open={showEngagementModal}
        onClose={() => setShowEngagementModal(false)}
        status={engagement}
      />
    </div>
    </div>
  );
}
